import React, { useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { Image, Button, OverlayTrigger, Tooltip } from 'react-bootstrap';
import { IconPlus, IconTrash, IconDownload } from '../Icons';
import ImageTooltip from '../ImageTooltip';
import { TechnicalSpecProduct } from '../../store/slices/technicalSpecsSlice';

interface ImagesCellProps {
  product: TechnicalSpecProduct;
  onUploadImage: (productId: string, imageFile: File, imageIndex: number) => Promise<void>;
  onDeleteImage: (productId: string, imageIndex: number) => Promise<void>;
  onSwapImages: (productId: string, fromIndex: number, toIndex: number) => Promise<void>;
}

interface ImageSlot {
  index: number;
  url?: string;
  isThumb: boolean;
  isEmpty: boolean;
}

const ImagesCell: React.FC<ImagesCellProps> = ({
  product,
  onUploadImage,
  onDeleteImage,
  onSwapImages
}) => {
  const [draggedIndex, setDraggedIndex] = useState<number | null>(null);
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null);
  const [imageTooltip, setImageTooltip] = useState<{
    show: boolean;
    imageUrl: string;
    alt: string;
    position: { x: number; y: number };
  }>({
    show: false,
    imageUrl: '',
    alt: '',
    position: { x: 0, y: 0 }
  });

  const fileInputRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [focusedSlot, setFocusedSlot] = useState<number | null>(null);



  // Функция для получения полного URL изображения
  const getFullImageUrl = useCallback((url: string) => {
    if (!url) return '';
    
    // Если URL уже полный (начинается с http), возвращаем как есть
    if (url.startsWith('http')) {
      return url;
    }
    
    // Если URL начинается с //, добавляем https:
    if (url.startsWith('//')) {
      return `https:${url}`;
    }
    
    // Если URL относительный (начинается с /), используем как есть
    // В разработке работает через proxy, в продакшене через Nginx Proxy Manager
    if (url.startsWith('/')) {
      return url;
    }
    
    // Для остальных случаев (абсолютные URL) возвращаем как есть
    return url;
  }, []);

  // Создаем массив всех слотов (включая пустые)
  const imageSlots = useMemo((): ImageSlot[] => {
    const slots: ImageSlot[] = [];
    
    // Слот для thumb_url (индекс 0)
    slots.push({
      index: 0,
      url: product.thumb_url ? getFullImageUrl(product.thumb_url) : undefined,
      isThumb: true,
      isEmpty: !product.thumb_url
    });
    
    // Слоты для image_url_1-4 (индексы 1-4)
    for (let i = 1; i <= 4; i++) {
      const imageUrl = (product as any)[`image_url_${i}`];
      slots.push({
        index: i,
        url: imageUrl ? getFullImageUrl(imageUrl) : undefined,
        isThumb: false,
        isEmpty: !imageUrl
      });
    }
    
    return slots;
  }, [product, getFullImageUrl]);

  // Обработка загрузки файла в конкретный слот
  const handleFileUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>, slotIndex: number) => {
    const file = event.target.files?.[0];
    if (file) {
      onUploadImage(product.id, file, slotIndex);
    }
    // Очищаем input для возможности повторной загрузки того же файла
    event.target.value = '';
  }, [product.id, onUploadImage]);



  // Обработка вставки из буфера в конкретный слот (для совместимости)
  const handlePaste = useCallback(async (event: React.ClipboardEvent, slotIndex: number) => {
    event.preventDefault();
    const items = event.clipboardData.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        const file = item.getAsFile();
        if (file) {
          console.log('Pasting image to slot', slotIndex, file);
          await onUploadImage(product.id, file, slotIndex);
        }
        break;
      }
    }
  }, [product.id, onUploadImage]);

  // Обработка удаления изображения
  const handleDeleteImage = useCallback(async (imageIndex: number) => {
    if (window.confirm('Удалить это изображение?')) {
      await onDeleteImage(product.id, imageIndex);
    }
  }, [product.id, onDeleteImage]);

  // Обработчики drag and drop
  const handleDragStart = useCallback((e: React.DragEvent, index: number) => {
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = 'move';
    // Скрываем tooltip при начале перетаскивания
    setImageTooltip({
      show: false,
      imageUrl: '',
      alt: '',
      position: { x: 0, y: 0 }
    });
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    // Проверяем, есть ли файлы в dataTransfer
    const hasFiles = e.dataTransfer.types.includes('Files');
    if (hasFiles) {
      e.dataTransfer.dropEffect = 'copy';
    } else {
      e.dataTransfer.dropEffect = 'move';
    }
    
    setDragOverIndex(targetIndex);
  }, []);

  const handleDrop = useCallback(async (e: React.DragEvent, targetIndex: number) => {
    e.preventDefault();
    
    // Проверяем, есть ли файлы в dataTransfer (drag & drop из файловой системы)
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      // Если есть файлы, загружаем первый изображение
      const imageFile = files.find(file => file.type.startsWith('image/'));
      if (imageFile) {
        console.log('Dropping file to slot', targetIndex, imageFile);
        await onUploadImage(product.id, imageFile, targetIndex);
      }
    } else if (draggedIndex !== null && draggedIndex !== targetIndex) {
      // Если нет файлов, но есть draggedIndex, то это перетаскивание между слотами
      setTimeout(async () => {
        try {
          await onSwapImages(product.id, draggedIndex, targetIndex);
        } catch (error) {
          console.error('Failed to swap images:', error);
        }
      }, 0);
    }
    
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, [draggedIndex, product.id, onSwapImages, onUploadImage]);

  const handleDragEnd = useCallback(() => {
    setDraggedIndex(null);
    setDragOverIndex(null);
  }, []);

  // Обработчик скачивания изображения
  const handleDownloadImage = useCallback(async (imageUrl: string, slotIndex: number) => {
    try {
      // Загружаем изображение как blob
      const response = await fetch(imageUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch image: ${response.status}`);
      }
      const blob = await response.blob();
      
      // Создаем URL для blob
      const blobUrl = URL.createObjectURL(blob);
      
      // Создаем временную ссылку для скачивания
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `image-${slotIndex}.jpg`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Освобождаем память
      URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Failed to download image:', error);
      // Fallback - открываем в новой вкладке
      window.open(imageUrl, '_blank');
    }
  }, []);

  // Обработчики для tooltip
  const handleImageMouseEnter = useCallback((imageUrl: string, alt: string, event: React.MouseEvent) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setImageTooltip({
      show: true,
      imageUrl,
      alt,
      position: {
        x: rect.left + rect.width / 2,
        y: rect.top
      }
    });
  }, []);

  const handleImageMouseLeave = useCallback(() => {
    setImageTooltip({
      show: false,
      imageUrl: '',
      alt: '',
      position: { x: 0, y: 0 }
    });
  }, []);

  return (
    <div 
      className="images-cell d-flex flex-wrap gap-1 align-items-center"
      style={{ minHeight: '60px', maxWidth: '300px' }}
    >
      {/* Отображение всех слотов */}
      {imageSlots.map((slot) => (
        <div 
          key={slot.index}
          className="position-relative d-flex flex-column align-items-center"
          style={{ width: '45px', height: '60px' }}
          draggable={!slot.isEmpty}
          onDragStart={(e) => handleDragStart(e, slot.index)}
          onDragOver={(e) => handleDragOver(e, slot.index)}
          onDrop={(e) => handleDrop(e, slot.index)}
          onDragEnd={handleDragEnd}
        >
          {/* Слот изображения */}
          <div 
            className="position-relative"
            style={{ 
              width: '45px', 
              height: '45px',
              border: dragOverIndex === slot.index ? '2px dashed #007bff' : 'none',
              borderRadius: '4px',
              backgroundColor: dragOverIndex === slot.index ? 'rgba(0, 123, 255, 0.1)' : 'transparent'
            }}
            onMouseEnter={() => setFocusedSlot(slot.index)}
            onMouseDown={() => setFocusedSlot(slot.index)}
            onPaste={(e) => handlePaste(e, slot.index)}
            tabIndex={0}
            onFocus={() => setFocusedSlot(slot.index)}
            onBlur={() => setFocusedSlot(null)}
            onKeyDown={(e) => {
              if (e.ctrlKey && e.key === 'v') {
                e.preventDefault();
                setFocusedSlot(slot.index);
                // Фокусируемся на слоте для paste
                e.currentTarget.focus();
              }
            }}
          >
            {slot.isEmpty ? (
              // Пустой слот
              <div 
                className="border border-dashed rounded d-flex align-items-center justify-content-center"
                style={{ 
                  width: '45px', 
                  height: '45px', 
                  backgroundColor: '#f8f9fa',
                  cursor: 'pointer'
                }}
                onClick={() => fileInputRefs.current[slot.index]?.click()}
                title={`Слот ${slot.index}${slot.isThumb ? ' (thumb)' : ''} - клик для загрузки, Ctrl+V для вставки`}
              >
                <IconPlus size={20} className="text-muted" />
              </div>
            ) : (
              // Заполненный слот
          <Image
                src={slot.url}
                alt={slot.isThumb ? 'Thumbnail' : `Изображение ${slot.index}`}
            width={45}
            height={45}
            className="rounded border"
                style={{ objectFit: 'cover', cursor: 'move' }}
                onMouseEnter={(e) => handleImageMouseEnter(slot.url!, slot.isThumb ? 'Thumbnail' : `Изображение ${slot.index}`, e)}
            onMouseLeave={handleImageMouseLeave}
            onError={(e) => {
                  console.error('Image load error:', slot.url);
              // Показываем placeholder при ошибке загрузки
              e.currentTarget.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDUiIGhlaWdodD0iNDUiIHZpZXdCb3g9IjAgMCA0NSA0NSIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQ1IiBoZWlnaHQ9IjQ1IiBmaWxsPSIjRjNGNEY2Ii8+CjxwYXRoIGQ9Ik0yMi41IDE1TDI3IDIwLjVMMjIuNSAyNkwxOCAyMC41TDIyLjUgMTVaIiBmaWxsPSIjOUI5QjlCIi8+Cjwvc3ZnPgo=';
              e.currentTarget.alt = 'Ошибка загрузки изображения';
            }}
          />
            )}
          
            {/* Кнопки управления для заполненных слотов */}
            {!slot.isEmpty && (
          <div className="position-absolute top-0 end-0 d-flex flex-column gap-1">
            <OverlayTrigger
              placement="top"
              overlay={<Tooltip>Удалить</Tooltip>}
            >
              <Button
                variant="danger"
                size="sm"
                className="p-1"
                style={{ width: '15px', height: '15px', fontSize: '10px',display: 'flex', flexDirection: 'row', alignContent: 'center', justifyContent: 'center', flexWrap: 'wrap' }}
                    onClick={() => handleDeleteImage(slot.index)}
              >
                <span style={{ verticalAlign: 'unset', lineHeight: 0 }}>
                  <IconTrash size={10} />
                </span>
              </Button>
            </OverlayTrigger>
          </div>
            )}
        </div>

        {/* Кнопка скачивания под слотом, если есть изображение */}
        {slot.url && (
          <button
            className="btn btn-outline-secondary btn-sm p-1 mt-1"
            style={{ width: '45px', fontSize: '8px' }}
            onClick={(e) => {
              e.stopPropagation();
              handleDownloadImage(slot.url!, slot.index);
            }}
            title="Скачать изображение"
          >
            <IconDownload size={8} />
          </button>
        )}


      {/* Скрытый input для загрузки файлов */}
      <input
            ref={(el) => {
              fileInputRefs.current[slot.index] = el;
            }}
        type="file"
        accept="image/*"
        style={{ display: 'none' }}
            onChange={(e) => handleFileUpload(e, slot.index)}
          />
        </div>
      ))}

      {/* Image Tooltip */}
      <ImageTooltip
        imageUrl={imageTooltip.imageUrl}
        alt={imageTooltip.alt}
        show={imageTooltip.show}
        position={imageTooltip.position}
      />
    </div>
  );
};

export default ImagesCell;